import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/Modal";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { TcIcon } from "@/components/TcIcon";
import { useToast } from "@/components/ToastProvider";
import {
  offsetShadow,
  TYPE,
  useCreateRoutine,
  useRunRoutine,
  useUpdateRoutine,
  TierRequiredError,
  GAME_PHASES,
  SITUATIONS,
  TIME_CONTROLS,
  type RoutineIn,
  type RoutineOut,
  type ScheduleKindId,
  type TimeControlId,
  type TriesPolicyId,
} from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";

const ALL_OPENINGS_FAMILIES = [
  "Italian Game", "Ruy Lopez", "Sicilian Defense", "Caro-Kann Defense", "French Defense",
  "Queen's Gambit Declined", "Queen's Pawn Game", "King's Pawn Game", "Slav Defense",
  "English Opening", "London System", "Four Knights Game",
];

// The clock + allowed tries each format imposes (mirrors the server gating).
const FIXED_CLOCK: Partial<Record<TimeControlId, number>> = {
  bullet: 5,
  blitz: 10,
  rapid: 30,
};
const ALLOWED_TRIES: Record<TimeControlId, TriesPolicyId[]> = {
  bullet: ["only_first"],
  blitz: ["only_first", "n_tries"],
  rapid: ["only_first", "n_tries", "infinite"],
  classical: ["only_first", "n_tries", "infinite"],
  none: ["only_first", "n_tries", "infinite"],
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function defaultDraft(): RoutineIn {
  return {
    name: "New training routine",
    enabled: true,
    drill_mode: "move",
    choice_difficulty: null,
    time_control: "blitz",
    tries_policy: "only_first",
    tries_n: null,
    time_limit_seconds: 10,
    phases: [],
    openings: [],
    situations: [],
    source_mix: 100,
    elo_min: null,
    elo_max: null,
    elo_all: false,
    length_kind: "mistakes",
    length_mistakes: 10,
    length_minutes: null,
    schedule_kind: "daily",
    schedule_weekdays: [],
    schedule_days_per_week: null,
    schedule_time_local: "08:00",
    schedule_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function toDraft(r: RoutineOut): RoutineIn {
  const { id: _id, created_at: _c, ...rest } = r;
  return rest;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initial?: RoutineOut | null;
  onUpgrade: () => void;
}

export function DailyBuilderModal({ open, onClose, initial, onUpgrade }: Props) {
  const p = useTheme();
  const nav = useNavigate();
  const toast = useToast();
  const createRoutine = useCreateRoutine();
  const updateRoutine = useUpdateRoutine();
  const runRoutine = useRunRoutine();
  const [draft, setDraft] = useState<RoutineIn>(initial ? toDraft(initial) : defaultDraft());

  useEffect(() => {
    if (open) setDraft(initial ? toDraft(initial) : defaultDraft());
  }, [open, initial]);

  function patch(next: Partial<RoutineIn>) {
    setDraft((d) => ({ ...d, ...next }));
  }

  function setTimeControl(tc: TimeControlId) {
    const allowed = ALLOWED_TRIES[tc];
    const policy: TriesPolicyId = allowed.includes(draft.tries_policy)
      ? draft.tries_policy
      : allowed[0];
    // Fixed-clock formats force their clock; switching to classical/none drops
    // any inherited fixed clock (default: no clock).
    const fromFixed = draft.time_control in FIXED_CLOCK;
    const clock = FIXED_CLOCK[tc] ?? (fromFixed ? null : (draft.time_limit_seconds ?? null));
    patch({
      time_control: tc,
      time_limit_seconds: clock,
      tries_policy: policy,
      tries_n: policy === "n_tries" ? (draft.tries_n ?? (tc === "blitz" ? 3 : 1)) : null,
    });
  }

  function toggleArr(key: "situations" | "openings" | "phases", val: string) {
    setDraft((d) => ({
      ...d,
      [key]: d[key].includes(val) ? d[key].filter((x) => x !== val) : [...d[key], val],
    }));
  }

  function toggleWeekday(i: number) {
    setDraft((d) => ({
      ...d,
      schedule_weekdays: d.schedule_weekdays.includes(i)
        ? d.schedule_weekdays.filter((x) => x !== i)
        : [...d.schedule_weekdays, i].sort((a, b) => a - b),
    }));
  }

  const allowedTries = ALLOWED_TRIES[draft.time_control];
  const clockFixed = draft.time_control in FIXED_CLOCK;

  async function save(thenStart: boolean) {
    try {
      const saved = initial?.id
        ? await updateRoutine.mutateAsync({ id: initial.id, body: draft })
        : await createRoutine.mutateAsync(draft);
      if (thenStart) {
        const session = await runRoutine.mutateAsync(saved.id);
        onClose();
        if (session.items.length === 0) {
          toast.push({ kind: "info", text: "No matching positions yet — import more games." });
          return;
        }
        nav(`/app/train/${session.id}`);
        return;
      }
      toast.push({ kind: "success", text: "Routine saved." });
      onClose();
    } catch (e) {
      if (e instanceof TierRequiredError) {
        toast.push({ kind: "danger", text: "Pro feature — upgrade to use this." });
        onUpgrade();
      } else {
        toast.push({ kind: "danger", text: (e as Error).message || "Could not save routine." });
      }
    }
  }

  const busy = createRoutine.isPending || updateRoutine.isPending || runRoutine.isPending;

  return (
    <Modal open={open} onClose={onClose} width={760} label="DAILY ROUTINE BUILDER">
      <div style={{ padding: "28px 24px 18px" }}>
        <input
          value={draft.name}
          onChange={(e) => patch({ name: e.target.value })}
          style={{
            width: "100%",
            fontFamily: TYPE.display,
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: -1,
            textTransform: "uppercase",
            background: "transparent",
            color: p.fg,
            border: "none",
            borderBottom: `2px dashed ${p.border}`,
            padding: "4px 0",
            outline: "none",
          }}
        />
      </div>

      {/* Schedule */}
      <Section p={p} label="SCHEDULE" sub="When this routine recurs.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(
            [
              ["daily", "Daily"],
              ["weekdays", "Specific days"],
              ["days_per_week", "N days / week"],
              ["none", "No schedule"],
            ] as [ScheduleKindId, string][]
          ).map(([kind, label]) => (
            <Chip key={kind} p={p} label={label} active={draft.schedule_kind === kind} onClick={() => patch({ schedule_kind: kind })} />
          ))}
        </div>
        {draft.schedule_kind === "weekdays" && (
          <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
            {WEEKDAYS.map((d, i) => (
              <Chip key={d} p={p} label={d} active={draft.schedule_weekdays.includes(i)} onClick={() => toggleWeekday(i)} />
            ))}
          </div>
        )}
        {draft.schedule_kind === "days_per_week" && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <Stepper
              p={p}
              value={draft.schedule_days_per_week ?? 3}
              min={1}
              max={7}
              onChange={(v) => patch({ schedule_days_per_week: v })}
            />
            <span style={{ fontFamily: TYPE.mono, fontSize: 12, color: p.fgMuted, fontWeight: 700 }}>days per week</span>
          </div>
        )}
      </Section>

      {/* Drill style */}
      <Section p={p} label="DRILL STYLE" sub="Find the move on the board, or pick the best from three timed options.">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: draft.drill_mode === "choice" ? 14 : 0 }}>
          <Chip
            p={p}
            label="Classic — find the move"
            active={draft.drill_mode === "move"}
            onClick={() => patch({ drill_mode: "move", choice_difficulty: null })}
          />
          <Chip
            p={p}
            label="Fast choice — pick of 3"
            active={draft.drill_mode === "choice"}
            onClick={() =>
              patch({
                drill_mode: "choice",
                choice_difficulty: draft.choice_difficulty ?? "medium",
                tries_policy: "only_first",
                tries_n: null,
              })
            }
          />
        </div>
        {draft.drill_mode === "choice" && (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>DIFFICULTY</div>
              <div style={{ display: "flex", gap: 6 }}>
                {(["easy", "medium", "hard"] as const).map((d) => (
                  <Chip key={d} p={p} label={d} active={draft.choice_difficulty === d} onClick={() => patch({ choice_difficulty: d })} />
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: p.fgMuted, marginBottom: 6 }}>SECONDS PER PUZZLE</div>
              <Stepper p={p} value={draft.time_limit_seconds ?? 15} min={2} max={60} onChange={(v) => patch({ time_limit_seconds: v })} />
            </div>
          </div>
        )}
      </Section>

      {/* Time control */}
      <Section p={p} label="TIME CONTROL" sub="The clock you'll train under — it sets your per-move limit and tries.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
          {TIME_CONTROLS.map((tc) => {
            const active = draft.time_control === tc.id;
            return (
              <button
                key={tc.id}
                onClick={() => setTimeControl(tc.id as TimeControlId)}
                style={{
                  padding: "14px 10px",
                  background: active ? p.fg : p.card,
                  color: active ? p.bg : p.fg,
                  border: `2px solid ${p.border}`,
                  boxShadow: active ? offsetShadow(p, 4, 4) : offsetShadow(p, 2, 2),
                  cursor: "pointer",
                  fontFamily: TYPE.body,
                  textAlign: "left",
                }}
              >
                <div style={{ marginBottom: 4 }}><TcIcon name={tc.icon} size={28} /></div>
                <div style={{ fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>{tc.label}</div>
                <div style={{ fontSize: 10.5, fontFamily: TYPE.mono, marginTop: 2, opacity: 0.7, fontWeight: 700, letterSpacing: 0.5 }}>{tc.detail}</div>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Tries + clock — N/A for choice drills (single-try, clock set above). */}
      {draft.drill_mode !== "choice" && (
      <Section p={p} label="TRIES & CLOCK" sub={clockFixed ? `${draft.time_control.toUpperCase()} fixes the clock at ${draft.time_limit_seconds}s per move.` : "Set any per-move clock (or none) for this format."}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <Chip
            p={p}
            label="Only first try counts"
            active={draft.tries_policy === "only_first"}
            onClick={() => patch({ tries_policy: "only_first", tries_n: null })}
          />

          {draft.time_control === "blitz" ? (
            // Blitz allows exactly 2 or 3 tries — discrete chips, no clicker.
            <>
              <Chip
                p={p}
                label="2 tries"
                active={draft.tries_policy === "n_tries" && draft.tries_n === 2}
                onClick={() => patch({ tries_policy: "n_tries", tries_n: 2 })}
              />
              <Chip
                p={p}
                label="3 tries"
                active={draft.tries_policy === "n_tries" && draft.tries_n === 3}
                onClick={() => patch({ tries_policy: "n_tries", tries_n: 3 })}
              />
            </>
          ) : (
            // Rapid / classical / no-limit: the "N tries" chip becomes a
            // - N + clicker once active.
            allowedTries.includes("n_tries") &&
            (draft.tries_policy === "n_tries" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: p.accent, color: p.accentFg, border: `2px solid ${p.border}`, boxShadow: offsetShadow(p, 3, 3), padding: "4px 10px" }}>
                <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, fontFamily: TYPE.body }}>✓</span>
                <Stepper p={p} value={draft.tries_n ?? 2} min={1} max={9} onChange={(v) => patch({ tries_n: v })} />
                <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, fontFamily: TYPE.body }}>tries</span>
              </div>
            ) : (
              <Chip
                p={p}
                label="N tries"
                active={false}
                onClick={() => patch({ tries_policy: "n_tries", tries_n: draft.tries_n ?? 2 })}
              />
            ))
          )}

          {allowedTries.includes("infinite") && (
            <Chip
              p={p}
              label="Infinite tries"
              active={draft.tries_policy === "infinite"}
              onClick={() => patch({ tries_policy: "infinite", tries_n: null })}
            />
          )}
        </div>

        {!clockFixed && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.8, color: p.fg, marginBottom: 10 }}>
              Select time for move
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Chip
                p={p}
                label={draft.time_limit_seconds == null ? "No clock" : "Timed"}
                active
                onClick={() => patch({ time_limit_seconds: draft.time_limit_seconds == null ? 30 : null })}
              />
              {draft.time_limit_seconds != null && (
                <>
                  <BentArrow color={p.danger} />
                  <Stepper p={p} value={draft.time_limit_seconds} min={5} max={300} step={5} onChange={(v) => patch({ time_limit_seconds: v })} />
                  <span style={{ fontFamily: TYPE.mono, fontSize: 12, color: p.fgMuted, fontWeight: 700 }}>sec / move</span>
                </>
              )}
            </div>
          </div>
        )}
      </Section>
      )}

      {/* Game phase */}
      <Section p={p} label={`GAME PHASE · ${draft.phases.length === 0 ? "any" : draft.phases.length}`} sub="Which part of the game to drill, or leave empty for any.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {GAME_PHASES.map((ph) => (
            <Chip key={ph.slug} p={p} label={ph.label} active={draft.phases.includes(ph.slug)} onClick={() => toggleArr("phases", ph.slug)} />
          ))}
        </div>
      </Section>

      {/* Situations */}
      <Section p={p} label={`SITUATIONS · ${draft.situations.length} selected`} sub="What patterns to drill. Pick as many as you like.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SITUATIONS.map((s) => (
            <Chip key={s.slug} p={p} label={s.label} active={draft.situations.includes(s.slug)} onClick={() => toggleArr("situations", s.slug)} />
          ))}
        </div>
      </Section>

      {/* Openings */}
      <Section p={p} label={`OPENINGS · ${draft.openings.length === 0 ? "any" : draft.openings.length}`} sub="Filter to specific opening systems, or leave empty for any.">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {ALL_OPENINGS_FAMILIES.map((o) => (
            <Chip key={o} p={p} label={o} active={draft.openings.includes(o)} onClick={() => toggleArr("openings", o)} />
          ))}
        </div>
      </Section>

      {/* Source mix */}
      <Section p={p} label="GAME SOURCE" sub="Mix your own blunders with positions other players blundered at your rating.">
        <Slider
          p={p}
          value={draft.source_mix}
          min={0}
          max={100}
          step={10}
          onChange={(v) =>
            patch(
              v >= 100
                ? { source_mix: v, elo_min: null, elo_max: null, elo_all: false }
                : {
                    source_mix: v,
                    // Default to ±100 when first enabling the peer pool.
                    elo_max: draft.elo_all ? null : (draft.elo_max ?? 100),
                  },
            )
          }
          leftLabel="100% YOUR GAMES"
          rightLabel="100% SIMILAR-RATED"
        />
        <div style={{ marginTop: 10, fontSize: 13, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, letterSpacing: 0.5 }}>
          <span style={{ color: p.fg, fontSize: 22, fontFamily: TYPE.display, fontWeight: 800 }}>{draft.source_mix}%</span> your games · {100 - draft.source_mix}% from others
        </div>
        {draft.source_mix < 100 && (
          <div style={{ marginTop: 16 }}>
            <BrLabel>OTHER PLAYERS MISTAKES ELO DIFFERENCE</BrLabel>
            <div style={{ marginTop: 10 }}>
              {/* 100 → 1500 in steps of 100, then a final step to ALL (1600). */}
              <Slider
                p={p}
                value={draft.elo_all ? 1600 : (draft.elo_max ?? 100)}
                min={100}
                max={1600}
                step={100}
                onChange={(v) => {
                  if (v >= 1600) patch({ elo_all: true, elo_min: null, elo_max: null });
                  else patch({ elo_all: false, elo_min: null, elo_max: v });
                }}
                leftLabel="±100"
                rightLabel="ALL"
              />
              <div style={{ marginTop: 8, fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted }}>
                {draft.elo_all
                  ? "Any rating difference"
                  : `Within ±${draft.elo_max ?? 100} of your rating`}
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* Length */}
      <Section p={p} label="SESSION LENGTH" sub="Cap by number of mistakes, by minutes, or both.">
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {(
            [
              ["mistakes", "By mistakes"],
              ["minutes", "By minutes"],
              ["both", "Both"],
            ] as const
          ).map(([k, label]) => (
            <Chip key={k} p={p} label={label} active={draft.length_kind === k} onClick={() => patch({ length_kind: k })} />
          ))}
        </div>
        {(draft.length_kind === "mistakes" || draft.length_kind === "both") && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Stepper p={p} value={draft.length_mistakes ?? 12} min={1} max={100} onChange={(v) => patch({ length_mistakes: v })} />
            <span style={{ fontFamily: TYPE.mono, fontSize: 12, color: p.fgMuted, fontWeight: 700 }}>mistakes</span>
          </div>
        )}
        {(draft.length_kind === "minutes" || draft.length_kind === "both") && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Stepper p={p} value={draft.length_minutes ?? 15} min={1} max={120} step={5} onChange={(v) => patch({ length_minutes: v })} />
            <span style={{ fontFamily: TYPE.mono, fontSize: 12, color: p.fgMuted, fontWeight: 700 }}>minutes</span>
          </div>
        )}
      </Section>

      {/* Footer */}
      <div style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", gap: 10, borderTop: `2px solid ${p.border}`, flexWrap: "wrap" }}>
        <BrBtn variant="ghost" withShadow={false} onClick={onClose}>Cancel</BrBtn>
        <div style={{ display: "flex", gap: 10 }}>
          <BrBtn variant="surface" disabled={busy} onClick={() => void save(false)}>Save & Run later</BrBtn>
          <BrBtn variant="accent" disabled={busy} onClick={() => void save(true)}>Save & Start now →</BrBtn>
        </div>
      </div>
    </Modal>
  );
}

type P = ReturnType<typeof useTheme>;

// A short bent arrow drawing the eye from the "Select time for move" label to
// the clock clicker.
function BentArrow({ color }: { color: string }) {
  return (
    <svg width="40" height="28" viewBox="0 0 40 28" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <path
        d="M4 4 C 4 16, 14 22, 30 22"
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
      <path d="M24 16 L 32 22 L 23 26 Z" fill={color} />
    </svg>
  );
}

function Section({ p, label, sub, children }: { p: P; label: string; sub?: string; children: React.ReactNode }) {
  return (
    <div style={{ borderTop: `2px solid ${p.border}`, padding: "22px 24px" }}>
      <BrLabel>{label}</BrLabel>
      {sub && <div style={{ fontSize: 12, color: p.fgMuted, marginTop: 4 }}>{sub}</div>}
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  );
}

function Chip({ p, label, active, disabled, onClick }: { p: P; label: string; active: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: "7px 12px",
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontFamily: TYPE.body,
        background: active ? p.accent : p.card,
        color: active ? p.accentFg : p.fg,
        border: `2px solid ${p.border}`,
        boxShadow: active ? offsetShadow(p, 3, 3) : offsetShadow(p, 2, 2),
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background .15s, box-shadow .15s",
      }}
    >
      {active && <span style={{ marginRight: 6 }}>✓</span>}
      {label}
    </button>
  );
}

function Stepper({ p, value, min, max, step = 1, onChange }: { p: P; value: number; min: number; max: number; step?: number; onChange: (v: number) => void }) {
  const btn = (label: string, delta: number) => (
    <button
      onClick={() => onChange(Math.max(min, Math.min(max, value + delta)))}
      style={{
        width: 34,
        height: 34,
        background: p.card,
        color: p.fg,
        border: `2px solid ${p.border}`,
        boxShadow: offsetShadow(p, 2, 2),
        cursor: "pointer",
        fontFamily: TYPE.mono,
        fontWeight: 800,
        fontSize: 16,
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {btn("−", -step)}
      <span style={{ minWidth: 28, textAlign: "center", fontFamily: TYPE.display, fontWeight: 800, fontSize: 20 }}>{value}</span>
      {btn("+", step)}
    </div>
  );
}

function Slider({
  p,
  value,
  min,
  max,
  step = 1,
  onChange,
  leftLabel,
  rightLabel,
}: {
  p: P;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  leftLabel?: string;
  rightLabel?: string;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      {(leftLabel || rightLabel) && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8, color: p.fgMuted }}>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
      <div style={{ position: "relative", height: 24 }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 10, transform: "translateY(-50%)", background: p.bg, border: `2px solid ${p.border}`, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: p.accent, borderRight: `2px solid ${p.border}` }} />
        </div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
        />
        <div style={{ position: "absolute", left: `calc(${pct}% - 12px)`, top: "50%", transform: "translateY(-50%)", width: 24, height: 24, background: p.fg, border: `2px solid ${p.border}`, boxShadow: offsetShadow(p, 2, 2), pointerEvents: "none" }} />
      </div>
    </div>
  );
}
